/**
 * Der MITLAUFENDE CTA — die Fläche mit Handlungsaufruf, die ab Slide X unten
 * am Bild erscheint und bis zum Ende stehenbleibt (Jans Auftrag 05.08.2026,
 * Kundenwunsch; Entwurfs-Vorlage: Benedikts „Mitlaufender CTA"-HTML).
 *
 * EIN Bauteil für alle Stellen — Pro-Editor-Bühne, Vorschau, veröffentlichter
 * Scrolly — aus demselben Grund wie CtaButton: getrennte Fassungen laufen
 * auseinander. WANN gezeichnet wird, entscheidet nicht dieses Bauteil, sondern
 * `resolveRunningCta` + `runningCtaSichtbar` (post-types/running-cta.ts).
 *
 * IMMER GEMOUNTET, Sichtbarkeit per CSS: Der Streifen GLEITET herein
 * (transform + opacity). Würde er erst beim Erreichen der Slide in den DOM
 * kommen, gäbe es nichts, WOVON er hereingleiten könnte — der erste Frame wäre
 * zugleich der Start- und Zielzustand. Unsichtbar fängt er keine Klicks
 * (`pointerEvents: none`).
 */
import { useEffect, useState } from "react";
import type { FunctionComponent } from "react";
import type { Color, CtaAbgabe, ResolvedRunningCta } from "@scrolly/post-types";
import { grundSchnitt, ZEILE_SCHNITT } from "@scrolly/post-types";
import CtaButton from "./CtaButton";
import RunningVote from "./RunningVote";
import RunningRating from "./RunningRating";
import RunningReaction from "./RunningReaction";

interface Props {
    strip: ResolvedRunningCta;
    /** Steht der Leser auf oder hinter der Start-Slide? (runningCtaSichtbar) */
    sichtbar: boolean;
    /** Im Editor führt der Knopf nirgendwohin und Schließen ist gesperrt. */
    editorMode?: boolean;
    /** Nur bei echten Lesern gesetzt (s. VisualStoryPlayer.onCtaAntwort). */
    onCtaAntwort?: (abgabe: CtaAbgabe) => void;
    /**
     * Herein/Hinaus macht der Stapel, am Scrollen festgemacht (RunningCtaStapel,
     * 27.09.2026). Dann gleitet der Streifen selbst nur noch beim Wegklicken (×).
     */
    gescrubbt?: boolean;
    /** REACTION: solange getippt wird, hält der Stapel den CTA fest. */
    onFesthalten?: (an: boolean) => void;
}

const farbe = (c: Color): string => `rgba(${c.r}, ${c.g}, ${c.b}, ${c.a})`;

/**
 * Hereingleiten des CTA (Jans iPhone-Funde 27.09.2026: „zu schnell", nach
 * 0,7 s „immer noch etwas zu schnell", nach 1,0 s „noch etwas langsamer"
 * — am iPhone über den lokalen Viewer abgestimmt): 1,4 s, weich auslaufend — kommt
 * zügig an und bremst sanft ab statt hart einzurasten.
 */
export const GLEIT_MS = 1400;
const GLEIT_KURVE = "cubic-bezier(0.22, 1, 0.36, 1)";

const RunningCtaStrip: FunctionComponent<Props> = ({
    strip,
    sichtbar,
    editorMode,
    onCtaAntwort,
    gescrubbt,
    onFesthalten,
}) => {
    /*
     * Vom Leser weggeräumt — nur für diese Ansicht, nichts wird gespeichert.
     * Im EDITOR gibt es das Schließen bewusst nicht: Wer den Streifen dort
     * wegklickt, sucht ihn danach vergeblich und hält das für einen Fehler
     * (dieselbe Überlegung wie der wirkungslose CTA-Klick im Editor).
     */
    const [geschlossen, setGeschlossen] = useState(false);
    // Weggeklickt gilt nur, solange der Leser in der Spanne ist (Jan 27.09.2026):
    // Scrollt er hinaus und zurück, ist der CTA wieder da — sonst wäre er z. B.
    // in der Template-Vorschau für immer verschwunden und man suchte ihn.
    useEffect(() => {
        if (!sichtbar) setGeschlossen(false);
    }, [sichtbar]);
    const zeigen = sichtbar && !geschlossen;
    // gescrubbt: nur das × lässt ihn selbst hinausgleiten
    const draussen = gescrubbt ? geschlossen : !zeigen;
    // VOTE (27.09.2026): gleiche Fläche, gleiches Hereingleiten — nur der
    // Inhalt ist die Abstimmung statt Zeile + Link-Knopf.
    const vote = strip.art === "vote" ? strip.abstimmung : null;
    const rating = strip.art === "rating" ? strip.bewertung : null;
    const reaktion = strip.art === "reaction" ? strip.reaktion : null;
    // VOTE, RATING und REACTION teilen sich die Machart: Spalte statt Zeile, × oben rechts
    const frage = Boolean(vote || rating || reaktion);
    // Innenabstand oben (und unten, wenn nicht volle Breite)
    const innen = frage ? 14 : 10;

    return (
        <div
            style={{
                display: "flex",
                justifyContent: "center",
                // unten mehr Luft (Jans iPhone-Fund 27.09.2026: „unten ein
                // bisschen eng") — 24 px statt 12; nach dem prod-Test
                // 03.10.2026 („etwas mehr Abstand vom unteren Rand", Safari
                // iOS 26 mit schwebender Adressleiste über dem Seitenende):
                // 32 px, auf Geräten mit Home-Balken 16 px über dessen Safe
                // Area (iPhone ≈ 50 px; der Viewer setzt viewport-fit=cover).
                padding: strip.fullWidth
                    ? 0
                    : "12px 12px max(32px, calc(16px + env(safe-area-inset-bottom, 0px)))",
                // Unsichtbar = nach unten hinausgeschoben + durchsichtig,
                // wie in Benedikts Entwurf (.cta-strip.hidden).
                // translate3d + willChange: eigene Grafikebene, damit das Handy
                // Fläche und Schatten beim Gleiten nicht Bild für Bild neu
                // zeichnet (Jans iPhone-Fund 27.09.2026).
                transform: draussen ? "translate3d(0, 110%, 0)" : "translate3d(0, 0, 0)",
                opacity: draussen ? 0 : 1,
                transition: `transform ${GLEIT_MS}ms ${GLEIT_KURVE}, opacity ${GLEIT_MS}ms ${GLEIT_KURVE}`,
                willChange: "transform, opacity",
                pointerEvents: zeigen ? "auto" : "none",
            }}
        >
            <div
                style={{
                    flex: strip.fullWidth ? 1 : undefined,
                    maxWidth: "100%",
                    display: "flex",
                    position: "relative",
                    flexDirection: frage || strip.layout === "column" ? "column" : "row",
                    alignItems: "center",
                    // IMMER mittig, auch bei voller Breite: Zeile und Knopf
                    // gehoeren als Paar in die Mitte (Jans Vorgabe 05.08.2026).
                    // space-between riss sie vorher an die Raender.
                    justifyContent: "center",
                    // 10 px: Jans Feinabstimmung 05.08.2026 („Abstand 10px
                    // besser") — Zeile und Knopf gehören als EIN Paar gelesen.
                    gap: frage ? "0" : strip.layout === "column" ? "4px" : "10px",
                    background: farbe(strip.fillColor),
                    borderRadius: `${strip.radius}px`,
                    // Innenabstand NUR als Einzelwerte (Jans Fund 03.10.2026: nach
                    // dem Ausschalten von „Volle Breite" klebte der Knopf unten am
                    // Rand). Kurzform `padding` + ein bedingtes `paddingBottom`
                    // vertragen sich in React nicht: Fällt paddingBottom weg, löscht
                    // React es — die unveränderte Kurzform setzt es nicht neu, unten
                    // blieb 0.
                    paddingTop: `${innen}px`,
                    paddingRight: "16px",
                    paddingLeft: "16px",
                    // Volle Breite liegt direkt an der Unterkante: dort kommt
                    // die Luft nach unten INNEN dazu (+12 px, mindestens über
                    // dem Home-Balken) — Jans iPhone-Fund 27.09.2026.
                    paddingBottom: strip.fullWidth
                        ? `max(${innen + 12}px, calc(${innen}px + env(safe-area-inset-bottom, 0px)))`
                        : `${innen}px`,
                    // Ohne Schattenkante ginge die Fläche auf hellen Bildern
                    // unter — derselbe Schutz wie in Benedikts Entwurf.
                    boxShadow: strip.fullWidth
                        ? "0 -6px 20px rgba(0,0,0,0.25)"
                        : "0 12px 28px rgba(0,0,0,0.35)",
                }}
            >
                {vote ? <RunningVote strip={strip} vote={vote} editorMode={editorMode} onCtaAntwort={onCtaAntwort} /> : null}
                {rating ? <RunningRating strip={strip} rating={rating} editorMode={editorMode} onCtaAntwort={onCtaAntwort} /> : null}
                {reaktion ? (
                    <RunningReaction
                        strip={strip}
                        reaktion={reaktion}
                        editorMode={editorMode}
                        onCtaAntwort={onCtaAntwort}
                        onFesthalten={onFesthalten}
                    />
                ) : null}

                {strip.zeile && strip.zeilenZiel ? (
                    /*
                     * Adresse ohne Knopf-Beschriftung (Lauras Fund 06.10.2026):
                     * Dann ist der ganze Streifen der Link — wer eine URL
                     * einträgt, will, dass der Streifen dorthin führt. Im
                     * Editor führt er nirgendwohin, wie der Knopf auch.
                     */
                    <a
                        className="scrolly-cta"
                        href={editorMode ? undefined : strip.zeilenZiel.href}
                        target={editorMode ? undefined : strip.zeilenZiel.target}
                        rel={editorMode ? undefined : strip.zeilenZiel.rel}
                        style={{
                            position: "absolute",
                            inset: 0,
                            borderRadius: "inherit",
                            cursor: editorMode ? "default" : "pointer",
                        }}
                        aria-label={strip.zeile}
                    />
                ) : null}

                {strip.zeile ? (
                    <div
                        style={{
                            color: farbe(strip.textColor),
                            fontSize: `${strip.fontSize}px`,
                            lineHeight: 1.3,
                            fontWeight: grundSchnitt(strip.fontWeight, ZEILE_SCHNITT),
                            textAlign: strip.layout === "column" ? "center" : "left",
                            ...(strip.fontFamily
                                ? { fontFamily: `'${strip.fontFamily}', sans-serif` }
                                : {}),
                        }}
                    >
                        {strip.zeile}
                    </div>
                ) : null}

                {strip.knopf ? (
                    /*
                     * Derselbe Knopf wie in der HTML-Box, aber OHNE dessen
                     * Abstand nach oben (`ohneAbstand`): Der gehört zur
                     * Box-Lage (Knopf unter dem Text); hier sitzt er mittig
                     * in der Zeile. Der frühere Gegen-Rand (-1em am Wrapper)
                     * verrechnete sich nicht sauber — Ränder kollabieren
                     * durch rahmenlose Wrapper — und drückte den Knopf nach
                     * unten (Jans Fund 05.08.2026).
                     */
                    <div style={{ flexShrink: 0 }}>
                        <CtaButton
                            cta={strip.knopf}
                            editorMode={editorMode}
                            ohneAbstand
                        />
                    </div>
                ) : null}

                {/* × auch im EDITOR zeigen (Jan 27.09.2026: man sah nicht, dass die
                    Option wirkt) — dort aber ohne Wirkung, sonst verschwindet der
                    CTA beim Arbeiten und man sucht ihn vergeblich. */}
                {strip.dismissible ? (
                    <button
                        type="button"
                        aria-label="Schließen"
                        title={editorMode ? "Der Leser kann den CTA hier schließen" : undefined}
                        onClick={editorMode ? undefined : () => setGeschlossen(true)}
                        style={{
                            flexShrink: 0,
                            // bei der Abstimmung oben rechts in der Ecke
                            // sonst über dem Zeilen-Link (zeilenZiel), der die Fläche füllt
                            ...(frage
                                ? { position: "absolute", top: "10px", right: "10px" }
                                : { position: "relative", zIndex: 1 }),
                            width: "26px",
                            height: "26px",
                            border: "none",
                            borderRadius: "50%",
                            background: "rgba(0,0,0,0.18)",
                            color: farbe(strip.textColor),
                            fontSize: "14px",
                            lineHeight: 1,
                            cursor: editorMode ? "default" : "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: 0,
                        }}
                    >
                        ×
                    </button>
                ) : null}
            </div>
        </div>
    );
};

export default RunningCtaStrip;
